import { Component, input, effect, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ConstantGradientViewModel, ConstantGradientCalcContext } from 'src/app/wellbore-inputs/models/undisturbed-temperature.model';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';

/**
 * Constant Gradient UDT Component (V2)
 * 
 * Uses ngx-sub-form to handle ControlValueAccessor automatically
 * Much simpler than manual implementation
 * 
 * Usage:
 * <app-udt-constant-gradient-v2 
 *   formControlName="constantGradient"
 *   [viewModel]="gradientViewModel()" />
 */
@Component({
    selector: 'app-udt-constant-gradient-v2',
    templateUrl: './udt-constant-gradient-v2.component.html',
    styles: [],
    providers: subformComponentProviders(UdtConstantGradientV2Component),
    standalone: false
})
export class UdtConstantGradientV2Component implements OnChanges {
    @Input()
    public userUnits: UserUnitsModel;

    @Input()
    public totalDepth: number;

    @Input()
    public mudlineDepth: number | undefined;
    
    public gradientUnit: string;
    public tempUnit: string;
    public depthUnit: string;

    // Signal input - the whole nested view model
    viewModel = input.required<ConstantGradientViewModel>();

    // ngx-sub-form handles ControlValueAccessor automatically
    form = createForm<ConstantGradientViewModel['data']>(this, {
        formType: FormType.SUB,
        formControls: {
            surfaceAmbientTemperature: new FormControl(null, {updateOn: 'blur'}),
            mudlineTemperature: new FormControl(null, {updateOn: 'blur'}),
            temperatureGradient: new FormControl(null, {updateOn: 'blur'}),
            temperatureAtTd: new FormControl(null, {updateOn: 'blur'})
        }
    });

    constructor() {
        // Initialize form from viewModel
        effect(() => {
            const vm = this.viewModel();
            if (vm?.data) {
                this.form.formGroup.patchValue(vm.data);
            }
        });

        // Subscribe to gradient changes and calculate temperature at TD
        this.form.formGroup.get('temperatureGradient')?.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(gradient => {
                const vm = this.viewModel();
                if (vm && gradient !== null) {
                    this.form.formGroup.patchValue({
                        temperatureAtTd: this.calculateTempAtTd(gradient, vm.calcContext)
                    }, { emitEvent: false });
                }
            });

        // Subscribe to temp at TD changes and calculate gradient
        this.form.formGroup.get('temperatureAtTd')?.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(tempAtTd => {
                const vm = this.viewModel();
                if (vm && tempAtTd !== null) {
                    this.form.formGroup.patchValue({
                        temperatureGradient: this.calculateGradient(tempAtTd, vm.calcContext)
                    }, { emitEvent: false });
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userUnits'] && this.userUnits) {
            this.gradientUnit = unitsLib[this.userUnits.temperatureGradient].symbol;
            this.tempUnit = this.userUnits.temperature;
            this.depthUnit = this.userUnits.longLengths;
        }
    }

    getControl(name: string): FormControl {
        return this.form.formGroup.get(name) as FormControl;
    }

    private calculateTempAtTd(gradient: number, ctx: ConstantGradientCalcContext): number {
        const depthDiff = ctx.totalDepthTvd - ctx.topTvd;
        return ctx.topTemperature + (gradient * depthDiff / ctx.gradientDenominator);
    }

    private calculateGradient(tempAtTd: number, ctx: ConstantGradientCalcContext): number {
        const depthDiff = ctx.totalDepthTvd - ctx.topTvd;
        return (tempAtTd - ctx.topTemperature) * ctx.gradientDenominator / depthDiff;
    }
}
